import { ServiceError } from '../errors'
import { readLocal, writeLocal } from '../local-store'
import { simulate } from '../mock-utils'
import { MOCK_CHANGE_PENDING_FIELD } from '../pagos/mock'
import { addMockRequest } from '../peticiones/mock'
import { money } from '../types'
import { toLocalIso } from './real'
import type { BookingPolicy, CancelQuote, ChangeInput, ChangeOption, ChangeOptions, ChangeQuote, Stay, TimeChoice, UnitChoice } from './types'

/**
 * Cambios de reserva simulados (8.5). Reproduce el bundle (Reserva.dc.html): Ático Plaza Mayor del 3 al 6 oct a
 * 450 €, noches de martes y miércoles a 180 € y el resto a 150 € (3–6 → 4–7 oct = +30 €), cambios gratis hasta
 * 48 h antes, cancelación gratis hasta el 26 sep y después se devuelve el 50 %. El cambio con coste sigue el
 * orden de la app actual (aplicar y después cobrar lo pendiente, D45) con el banco simulado del bloque 3.
 *
 * Códigos de reserva (combinables con los de `mock.ts` y `pagos/mock.ts`):
 *  - `cambio-no-disponible` (las fechas elegidas están ocupadas), `cambio-falla` (la cotización falla),
 *    `cambio-mas-barato` (la diferencia es a favor), `cambio-aplicar-falla` (falla modifyreserve),
 *    `reversion-falla` (falla volver a las fechas originales tras un cobro fallido, D45)
 *  - `sin-cambios` (el canal no permite cambios desde la app), `opciones-falla` (fallan las entradas de cambio)
 *  - `cancelar-gratis`, `cancelar-con-gastos`, `cancelar-falla`, `peticion-falla`
 *  - cobro de la diferencia: `pago-ok`, `pago-rechazado`, `pago-tiempo`, `pago-directo`, `pago-falla`
 */

/** Estado de la reserva simulada tras los cambios, guardado en el dispositivo por guestCode. */
export interface MockBookingState {
  checkIn?: string | undefined
  checkOut?: string | undefined
  adults?: number | undefined
  children?: number | undefined
  unitId?: string | undefined
  unitName?: string | undefined
  /** Suma de las diferencias aplicadas (céntimos) */
  priceDelta?: number | undefined
  cancelled?: boolean | undefined
}

export const MOCK_BOOKING_FIELD = 'mockBooking'

export const readMockBooking = (guestCode: string): MockBookingState => readLocal<MockBookingState>(guestCode, MOCK_BOOKING_FIELD) ?? {}
const writeMockBooking = (guestCode: string, patch: MockBookingState) => {
  const defined = Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== undefined))
  writeLocal(guestCode, MOCK_BOOKING_FIELD, { ...readMockBooking(guestCode), ...defined })
}

/** Aplica al mock de `getStay` los cambios hechos en este dispositivo. */
export function withMockChanges(stay: Stay, guestCode: string): Stay {
  const b = readMockBooking(guestCode)
  if (Object.keys(b).length === 0) return stay
  const checkIn = b.checkIn ?? stay.dates.checkIn
  const checkOut = b.checkOut ?? stay.dates.checkOut
  const adults = b.adults ?? stay.guests.adults
  const children = b.children ?? stay.guests.children
  return {
    ...stay,
    dates: { checkIn, checkOut, nights: nightsBetween(isoDay(checkIn), isoDay(checkOut)) },
    guests: { ...stay.guests, adults, children },
    registration: { ...stay.registration, total: adults + children },
    unit: b.unitId ? { ...stay.unit, roomId: b.unitId, name: b.unitName ?? stay.unit.name } : stay.unit,
    totalPrice: stay.totalPrice && b.priceDelta ? money(stay.totalPrice.amount + b.priceDelta, stay.totalPrice.currency) : stay.totalPrice,
    flags: b.cancelled ? { ...stay.flags, cancelled: true } : stay.flags,
  }
}

/* ── fechas ── */

const pad = (n: number) => String(n).padStart(2, '0')
/** `yyyy-MM-dd` del día local de un ISO con hora. */
export const isoDay = (iso: string) => {
  const d = new Date(iso)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
const parseDay = (day: string) => {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  return new Date(y, m - 1, d)
}
const addDays = (day: string, n: number) => {
  const d = parseDay(day)
  d.setDate(d.getDate() + n)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
const nightsBetween = (from: string, to: string) => Math.round((parseDay(to).getTime() - parseDay(from).getTime()) / 86400000)
/** Noches de un rango (el día de salida no cuenta). */
const nightsOf = (from: string, to: string) => Array.from({ length: Math.max(0, nightsBetween(from, to)) }, (_, i) => addDays(from, i))
const timeOf = (iso: string) => {
  const d = new Date(iso)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}
const withTime = (day: string, time: string) => toLocalIso(day.split('-').reverse().join('-'), time)
const es = (day: string, options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('es-ES', options).format(parseDay(day))

/* ── datos del ejemplo ── */

const isHotel = (stay: Stay) => stay.id === 'R-30412'

/** Precio de una noche: martes y miércoles más caros (bundle: "la noche del martes 6 es más cara"). */
function nightPrice(stay: Stay, day: string): number {
  if (isHotel(stay)) return 18000
  const weekday = parseDay(day).getDay()
  return weekday === 2 || weekday === 3 ? 18000 : 15000
}

/** Ocupado todos los meses del 10 al 12 y del 24 al 25 (salvo las noches de la propia reserva). */
function busyNights(stay: Stay, from: string, to: string, extra: string[] = []): Set<string> {
  const own = new Set(nightsOf(isoDay(stay.dates.checkIn), isoDay(stay.dates.checkOut)))
  const busy = new Set(extra)
  for (const day of nightsOf(from, addDays(to, 1))) {
    const d = parseDay(day).getDate()
    if (((d >= 10 && d <= 12) || d === 24 || d === 25) && !own.has(day)) busy.add(day)
  }
  return busy
}

export function mockPolicy(stay: Stay): BookingPolicy {
  if (isHotel(stay)) return { changeFreeUntil: null, freeCancel: false, cancelFreeUntil: null, refundPercentAfter: 0, conditions: 'Tarifa no reembolsable.' }
  return {
    changeFreeUntil: new Date(new Date(stay.dates.checkIn).getTime() - 48 * 3600 * 1000).toISOString(),
    freeCancel: true,
    cancelFreeUntil: '2026-09-26T23:59:00+02:00',
    refundPercentAfter: 50,
    conditions: 'Cancelación gratuita hasta el 26 de septiembre. Después se devuelve el 50 % de lo pagado. Los cambios de fechas son gratis hasta 48 horas antes de la llegada.',
  }
}

const UNITS: Record<string, UnitChoice[]> = {
  demo: [
    { id: 'U-ESTUDIO', name: 'Estudio Plaza Mayor', maxGuests: 2, difference: money(-9000) },
    { id: 'U-DUPLEX', name: 'Dúplex Cava Baja', maxGuests: 4, difference: money(9000) },
  ],
  hotel: [{ id: 'U-305', name: 'Habitación 305 · Doble con vistas al mar', maxGuests: 2, difference: money(9000) }],
}
const EARLY: TimeChoice[] = [
  { time: '13:00', price: money(2000) },
  { time: '14:00', price: money(1500) },
]
const LATE: TimeChoice[] = [
  { time: '12:00', price: money(1500) },
  { time: '14:00', price: money(2500) },
]

export function mockChangeOptions(stay: Stay, guestCode: string): ChangeOptions {
  if (guestCode.includes('opciones-falla')) throw new ServiceError('server', 'mock options error')
  const policy = mockPolicy(stay)
  const base = { allowed: true, blockedBy: null, price: null, time: null, maxGuests: null }
  const byChannel = isHotel(stay) || guestCode.includes('sin-cambios')
  const blocked = { ...base, allowed: false, blockedBy: 'channel' as const }
  const options: ChangeOption[] = [
    { type: 'dates', ...(byChannel ? blocked : base) },
    { type: 'nights', ...(byChannel ? blocked : base) },
    { type: 'guests', ...(guestCode.includes('sin-cambios') ? blocked : base), maxGuests: 3 },
    { type: 'unit', ...(guestCode.includes('sin-cambios') ? blocked : base) },
    ...(isHotel(stay) ? [] : [{ type: 'early_checkin' as const, ...base, price: EARLY[0]?.price ?? null, time: EARLY[0]?.time ?? null }]),
    { type: 'late_checkout', ...base, price: LATE[LATE.length - 1]?.price ?? null, time: LATE[LATE.length - 1]?.time ?? null },
    { type: 'cancel', ...base },
  ]
  return { policy, options, canRequest: true, requestResponseHours: 24 }
}

export function mockAvailability(stay: Stay, guestCode: string, from: string, to: string): { busyDates: string[] } {
  // Con `cambio-no-disponible` el calendario no avisa: el aviso llega al cotizar (como el bundle, 10–13 oct).
  if (guestCode.includes('cambio-no-disponible')) return { busyDates: [] }
  return { busyDates: [...busyNights(stay, from, to)].sort() }
}

export const mockUnitChoices = (stay: Stay): UnitChoice[] => (UNITS[isHotel(stay) ? 'hotel' : 'demo'] ?? []).filter((u) => u.maxGuests >= stay.guests.adults + stay.guests.children || isHotel(stay))
export const mockTimeChoices = (type: 'early_checkin' | 'late_checkout'): TimeChoice[] => (type === 'early_checkin' ? EARLY : LATE)

/* ── cotización ── */

const emptyQuote = (input: ChangeInput, currency: string): ChangeQuote => ({
  input,
  // Como E11: sin identificador de cotización ni caducidad (D45).
  quoteId: null,
  expiresAt: null,
  available: true,
  busy: null,
  alternatives: [],
  newCheckIn: null,
  newCheckOut: null,
  nights: null,
  unitName: null,
  newPrice: null,
  currentPrice: null,
  alreadyPaid: null,
  pendingAfter: null,
  changeFee: null,
  difference: money(0, currency),
  reason: null,
  conditions: null,
  confirmable: true,
  // La app actual no envía recibo del cambio, no avisa al canal ni promete devolución (D45).
  sendsReceipt: false,
  notifiesChannel: false,
  refundEtaDays: null,
})

function alternativesFor(from: string, nights: number, busy: Set<string>): { from: string; to: string }[] {
  const today = isoDay(new Date().toISOString())
  const fits = (start: string) => start >= today && nightsOf(start, addDays(start, nights)).every((n) => !busy.has(n))
  const found: { from: string; to: string }[] = []
  for (let i = 1; i <= 21 && !found.some((a) => a.from < from); i++) {
    const start = addDays(from, -i)
    if (fits(start)) found.push({ from: start, to: addDays(start, nights) })
  }
  for (let i = 1; i <= 21; i++) {
    const start = addDays(from, i)
    if (fits(start)) {
      found.push({ from: start, to: addDays(start, nights) })
      break
    }
  }
  return found.sort((a, b) => a.from.localeCompare(b.from))
}

function reasonForDates(stay: Stay, from: string, to: string, difference: number, feeFree: boolean): string | null {
  if (difference === 0) return null
  const oldNights = nightsOf(isoDay(stay.dates.checkIn), isoDay(stay.dates.checkOut))
  const newNights = nightsOf(from, to)
  const tail = `${feeFree ? ' El cambio en sí es gratis porque faltan más de 48 horas.' : ''} La fianza y el registro no cambian.`
  if (difference < 0) return `Las noches nuevas son más baratas que las que tenías.${tail}`
  const added = newNights.filter((n) => !oldNights.includes(n)).sort((a, b) => nightPrice(stay, b) - nightPrice(stay, a))[0]
  const removed = oldNights.filter((n) => !newNights.includes(n)).sort((a, b) => nightPrice(stay, a) - nightPrice(stay, b))[0]
  const name = (day: string) => `${es(day, { weekday: 'long' })} ${parseDay(day).getDate()}`
  if (added && removed && nightPrice(stay, added) > nightPrice(stay, removed)) return `La noche del ${name(added)} es más cara que la del ${name(removed)}.${tail}`
  if (added && !removed) return `Pagas las noches que añades.${tail}`
  return tail.trim()
}

export function mockQuote(stay: Stay, guestCode: string, input: ChangeInput): ChangeQuote {
  if (guestCode.includes('cambio-falla')) throw new ServiceError('server', 'mock quote error')
  const currency = stay.currency
  const current = stay.totalPrice ?? money(0, currency)
  const quote = emptyQuote(input, currency)
  const policy = mockPolicy(stay)
  const feeFree = !policy.changeFreeUntil || Date.now() < new Date(policy.changeFreeUntil).getTime()
  const fee = money(feeFree ? 0 : 2000, currency)
  const paid = isHotel(stay) ? null : current
  const priced = (newPrice: number, extra: Partial<ChangeQuote>): ChangeQuote => {
    const difference = extra.difference ?? money(newPrice - current.amount + fee.amount, currency)
    return { ...quote, newPrice: money(newPrice, currency), currentPrice: current, alreadyPaid: paid, changeFee: fee, ...extra, difference, pendingAfter: difference.amount > 0 ? difference : null }
  }

  switch (input.type) {
    case 'dates':
    case 'nights': {
      const { checkIn: from, checkOut: to } = input
      const nights = nightsBetween(from, to)
      const requested = nightsOf(from, to)
      const forced = guestCode.includes('cambio-no-disponible') ? requested : []
      const busy = busyNights(stay, addDays(from, -30), addDays(to, 30), forced)
      const blocked = requested.filter((n) => busy.has(n))
      if (blocked.length > 0) {
        const first = blocked[0] ?? from
        const last = blocked[blocked.length - 1] ?? from
        return { ...quote, available: false, confirmable: false, busy: { from: first, to: addDays(last, 1) }, alternatives: alternativesFor(from, nights, busy) }
      }
      let price = requested.reduce((sum, n) => sum + nightPrice(stay, n), 0)
      if (guestCode.includes('cambio-mas-barato')) price = current.amount - 3000
      const newCheckIn = withTime(from, timeOf(stay.dates.checkIn))
      const newCheckOut = withTime(to, timeOf(stay.dates.checkOut))
      return priced(price, { newCheckIn, newCheckOut, nights, reason: reasonForDates(stay, from, to, price - current.amount, feeFree && fee.amount === 0) })
    }
    case 'guests': {
      const before = stay.guests.adults + stay.guests.children
      const after = input.adults + input.children
      const perNight = isHotel(stay) ? 2500 : 0
      const price = current.amount + (after - before) * perNight * stay.dates.nights
      const reason = perNight === 0 ? 'El precio del alojamiento no cambia con el número de personas. La fianza no cambia.' : `Cada persona más son 25 € por noche. La fianza no cambia.`
      return priced(price, { nights: stay.dates.nights, newCheckIn: stay.dates.checkIn, newCheckOut: stay.dates.checkOut, reason, changeFee: money(0, currency), difference: money(price - current.amount, currency) })
    }
    case 'unit': {
      const unit = mockUnitChoices(stay).find((u) => u.id === input.unitId)
      if (!unit) return { ...quote, available: false, confirmable: false }
      const price = current.amount + unit.difference.amount
      return priced(price, { unitName: unit.name, nights: stay.dates.nights, newCheckIn: stay.dates.checkIn, newCheckOut: stay.dates.checkOut, reason: 'El precio cambia por el tamaño del alojamiento. La fianza y el registro no cambian.' })
    }
    case 'early_checkin':
    case 'late_checkout': {
      const choice = mockTimeChoices(input.type).find((c) => c.time === input.time)
      if (!choice) return { ...quote, available: false, confirmable: false }
      const early = input.type === 'early_checkin'
      return {
        ...quote,
        newCheckIn: early ? withTime(isoDay(stay.dates.checkIn), choice.time) : stay.dates.checkIn,
        newCheckOut: early ? stay.dates.checkOut : withTime(isoDay(stay.dates.checkOut), choice.time),
        changeFee: money(0, currency),
        difference: choice.price,
        pendingAfter: choice.price,
        reason: null,
      }
    }
  }
}

/* ── aplicación y reversión (el cobro lo orquesta change-payment.ts, D45) ── */

const SNAPSHOTS_FIELD = 'mockChangeSnapshots'

function stateFor(stay: Stay, quote: ChangeQuote): MockBookingState {
  const { input } = quote
  const delta = (readMockBooking(stay.guestCode).priceDelta ?? 0) + quote.difference.amount
  switch (input.type) {
    case 'dates':
    case 'nights':
      return { checkIn: quote.newCheckIn ?? undefined, checkOut: quote.newCheckOut ?? undefined, priceDelta: delta }
    case 'guests':
      return { adults: input.adults, children: input.children, priceDelta: delta }
    case 'unit':
      return { unitId: input.unitId, unitName: quote.unitName ?? undefined, priceDelta: delta }
    case 'early_checkin':
      return { checkIn: quote.newCheckIn ?? undefined, priceDelta: delta }
    case 'late_checkout':
      return { checkOut: quote.newCheckOut ?? undefined, priceDelta: delta }
  }
}

/**
 * Como `modifyreserve`: aplica el cambio y deja la diferencia pendiente de pago en el mock de pagos. Guarda cómo
 * estaba la reserva para poder revertirlo. `cambio-aplicar-falla` hace fallar la aplicación.
 */
export async function mockApply(stay: Stay, quote: ChangeQuote, getStay: (guestCode: string) => Promise<Stay>): Promise<Stay> {
  const g = stay.guestCode
  await simulate(g, 'reserva')
  if (g.includes('cambio-aplicar-falla')) throw new ServiceError('server', 'mock apply error')
  const before = { booking: readMockBooking(g), pending: readLocal<number>(g, MOCK_CHANGE_PENDING_FIELD) ?? 0 }
  writeLocal(g, SNAPSHOTS_FIELD, { ...(readLocal<Record<string, typeof before>>(g, SNAPSHOTS_FIELD) ?? {}), latest: before })
  writeMockBooking(g, stateFor(stay, quote))
  if (quote.difference.amount > 0) writeLocal(g, MOCK_CHANGE_PENDING_FIELD, before.pending + quote.difference.amount)
  return getStay(g)
}

/** Vuelve a como estaba antes del último cambio. `reversion-falla` la hace fallar. */
export async function mockRevert(stay: Stay, getStay: (guestCode: string) => Promise<Stay>): Promise<Stay> {
  const g = stay.guestCode
  await simulate(g, 'reserva')
  if (g.includes('reversion-falla')) throw new ServiceError('server', 'mock revert error')
  const snapshot = readLocal<Record<string, { booking: MockBookingState; pending: number }>>(g, SNAPSHOTS_FIELD)?.latest
  if (!snapshot) throw new ServiceError('server', 'nothing to revert')
  writeLocal(g, MOCK_BOOKING_FIELD, snapshot.booking)
  writeLocal(g, MOCK_CHANGE_PENDING_FIELD, snapshot.pending || null)
  return getStay(g)
}

/* ── cancelación y petición ── */

export function mockCancelQuote(stay: Stay, guestCode: string): CancelQuote {
  const currency = stay.currency
  const policy = mockPolicy(stay)
  const paidAmount = isHotel(stay) ? 27000 : (stay.totalPrice?.amount ?? 45000)
  const paid = money(paidAmount, currency)
  const afterDeadline = policy.cancelFreeUntil ? Date.now() > new Date(policy.cancelFreeUntil).getTime() : true
  const withPenalty = guestCode.includes('cancelar-con-gastos') || (!guestCode.includes('cancelar-gratis') && afterDeadline)
  const refundPercent = withPenalty ? (policy.refundPercentAfter ?? 0) : 100
  const refund = money(Math.round((paidAmount * refundPercent) / 100), currency)
  const modifiable = mockChangeOptions(stay, guestCode).options.find((o) => o.type === 'dates')?.allowed ?? false
  const changeFreeOpen = policy.changeFreeUntil !== null && Date.now() < new Date(policy.changeFreeUntil).getTime()
  return {
    paid,
    penalty: money(paidAmount - refund.amount, currency),
    refund,
    penaltyRule: policy.cancelFreeUntil && policy.refundPercentAfter !== null ? { refundPercent: policy.refundPercentAfter, after: policy.cancelFreeUntil } : null,
    depositReleased: true,
    refundEtaDays: { min: 3, max: 10 },
    conditions: policy.conditions,
    changeDatesFreeUntil: modifiable && changeFreeOpen ? policy.changeFreeUntil : null,
  }
}

export async function mockCancel(stay: Stay): Promise<void> {
  await simulate(stay.guestCode, 'reserva')
  if (stay.guestCode.includes('cancelar-falla')) throw new ServiceError('server', 'mock cancel error')
  writeMockBooking(stay.guestCode, { cancelled: true })
}

export async function mockRequestChange(stay: Stay, input: { type: string; text: string }): Promise<{ requestId: string }> {
  await simulate(stay.guestCode, 'reserva')
  if (stay.guestCode.includes('peticion-falla')) throw new ServiceError('server', 'mock request error')
  return { requestId: addMockRequest(stay.guestCode, { text: input.text, changeType: input.type }).id }
}
